import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { timeCustomersAPI, timeLeadsAPI } from '../services/api';
import { TimeCustomerLookup, TimeLead, TimeLeadTag } from '../types';
import { LEAD_STATUSES, toDateInputValue } from './LeadForm';
import LeadActivities from './LeadActivities';

const statusLabel = (statusId: number) => LEAD_STATUSES.find(status => status.id === statusId)?.label ?? `Status ${statusId}`;

const LEADS_PAGE_SIZE = 10000;

const LeadsPage: React.FC = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<TimeLead[]>([]);
  const [activitiesLead, setActivitiesLead] = useState<TimeLead | null>(null);
  const activitiesCloseButton = useRef<HTMLButtonElement>(null);
  const [activitySummaryError, setActivitySummaryError] = useState('');
  const [customers, setCustomers] = useState<TimeCustomerLookup[]>([]);
  const [tags, setTags] = useState<TimeLeadTag[]>([]);

  const [searchText, setSearchText] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [tagFilter, setTagFilter] = useState<number[]>([]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const listRequestId = useRef(0);

  const loadLookups = useCallback(async () => {
    const [customerResult, tagResult] = await Promise.allSettled([
      timeCustomersAPI.getCustomers(),
      timeLeadsAPI.getTags(),
    ]);
    if (customerResult.status === 'fulfilled') {
      setCustomers(customerResult.value);
    } else {
      console.error('[FIX:leads] Failed to load Flow customers', customerResult.reason);
    }
    if (tagResult.status === 'fulfilled') {
      setTags(tagResult.value);
    } else {
      console.error('[FIX:leads] Failed to load lead tags', tagResult.reason);
    }
  }, []);

  const loadLeads = useCallback(async () => {
    const requestId = ++listRequestId.current;
    setLoading(true);
    setLoadError('');
    try {
      const result = await timeLeadsAPI.getLeads({
        take: LEADS_PAGE_SIZE,
        searchText: searchText.trim() || undefined,
        customerId: customerFilter ? Number(customerFilter) : undefined,
        tagIds: tagFilter.length > 0 ? tagFilter : undefined,
      });
      if (requestId === listRequestId.current) {
        setLeads(result.items);
      } else if (process.env.NODE_ENV === 'development') {
        console.debug('[FIX:leads] Ignored superseded list response', { requestId });
      }
    } catch (error) {
      if (requestId === listRequestId.current) {
        console.error('[FIX:leads] Failed to load lead list', error);
        setLoadError('Leads could not be loaded.');
      }
    } finally {
      if (requestId === listRequestId.current) setLoading(false);
    }
  }, [searchText, customerFilter, tagFilter]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    loadLeads();
    return () => { listRequestId.current += 1; };
  }, [loadLeads]);

  useEffect(() => {
    if (!activitiesLead) return;
    const trigger = document.activeElement as HTMLElement | null;
    activitiesCloseButton.current?.focus();
    return () => { trigger?.focus(); };
  }, [activitiesLead]);

  const toggleTagFilter = (tagId: number) => {
    setTagFilter(current => (current.includes(tagId) ? current.filter(id => id !== tagId) : [...current, tagId]));
  };

  const openCreateForm = () => {
    navigate('/leads/new');
  };

  const openEditForm = (lead: TimeLead) => {
    navigate(`/leads/edit/${lead.id}`);
  };

  const refreshActivitySummary = async (leadId: number) => {
    const requestId = listRequestId.current;
    setActivitySummaryError('');
    try {
      const updated = await timeLeadsAPI.getLead(leadId);
      if (requestId === listRequestId.current) {
        setLeads(current => current.map(lead => lead.id === leadId ? updated : lead));
      }
    } catch (error) {
      console.error('Failed to refresh lead activity summary', error);
      setActivitySummaryError('Activities changed, but the lead summary could not be refreshed. Reload the list to retry.');
    }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1>Leads</h1>
          <p>Shared Flow projects in Lead, Follow-up, or Confirmed status.</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={openCreateForm}>
          New lead
        </button>
      </div>

      <div className="filter-bar">
        <input
          type="search"
          className="form-control"
          aria-label="Search leads"
          placeholder="Search leads..."
          value={searchText}
          onChange={event => setSearchText(event.target.value)}
        />
        <select
          className="form-select"
          aria-label="Filter by customer"
          value={customerFilter}
          onChange={event => setCustomerFilter(event.target.value)}
        >
          <option value="">All customers</option>
          {customers.map(customer => (
            <option key={customer.id} value={customer.id}>
              {customer.title}
            </option>
          ))}
        </select>
      </div>

      {tags.length > 0 && (
        <fieldset className="filter-bar">
          <legend className="text-muted">Filter by tags</legend>
          {tags.map(tag => (
            <label key={tag.id} className="toggle-row">
              <input
                type="checkbox"
                checked={tagFilter.includes(tag.id)}
                onChange={() => toggleTagFilter(tag.id)}
              />
              {tag.name}
            </label>
          ))}
          {tagFilter.length > 0 && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setTagFilter([])}
            >
              Clear tags
            </button>
          )}
        </fieldset>
      )}

      {activitySummaryError && <p role="alert">{activitySummaryError}</p>}

      {loading ? (
        <p role="status">Loading leads...</p>
      ) : loadError ? (
        <div role="alert" className="card">
          <p>{loadError}</p>
          <button type="button" className="btn btn-secondary" onClick={loadLeads}>
            Retry
          </button>
        </div>
      ) : (
        <>
          {leads.length === 0 ? (
            <div className="card">
              <p>No leads found. Try adjusting your filters or create a new lead.</p>
            </div>
          ) : (
            <div className="card">
              <div className="table-responsive">
                <table className="table table-hover" aria-label="Leads">
                  <thead>
                    <tr>
                      <th scope="col">Title</th>
                      <th scope="col">Customer</th>
                      <th scope="col">Status</th>
                      <th scope="col">Last activity</th>
                      <th scope="col">Activity title</th>
                      <th scope="col">Tags</th>
                      <th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map(lead => (
                      <tr
                        key={lead.id}
                        className={lead.lastActivityDate ? undefined : 'row-no-activity'}
                        title={lead.lastActivityDate ? undefined : 'No activities tracked'}
                      >
                        <td data-label="Title">{lead.title}</td>
                        <td data-label="Customer">{lead.customerTitle}</td>
                        <td data-label="Status">{statusLabel(lead.statusId)}</td>
                        <td data-label="Last activity">{lead.lastActivityDate ? toDateInputValue(lead.lastActivityDate) : '—'}</td>
                        <td data-label="Activity title">{lead.lastActivityTitle || '—'}</td>
                        <td data-label="Tags">{lead.tags.map(tag => tag.name).join(', ') || '—'}</td>
                        <td data-label="Actions">
                          <div className="action-row">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              aria-label={`View activities for ${lead.title}`}
                              aria-haspopup="dialog"
                              onClick={() => setActivitiesLead(lead)}
                            >
                              Activities
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              aria-label={`Edit ${lead.title}`}
                              onClick={() => openEditForm(lead)}
                            >
                              Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </>
      )}

      {activitiesLead && (
        <div
          className="time-modal-backdrop"
          onMouseDown={event => {
            if (event.target === event.currentTarget) setActivitiesLead(null);
          }}
        >
          <section
            className="time-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lead-activities-dialog-title"
            onKeyDown={event => {
              if (event.key === 'Escape') {
                event.stopPropagation();
                setActivitiesLead(null);
              }
              if (event.key === 'Tab') {
                const focusable = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(
                  'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]'
                ));
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                  event.preventDefault();
                  last?.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                  event.preventDefault();
                  first?.focus();
                }
              }
            }}
          >
            <div className="time-modal-header">
              <h2 id="lead-activities-dialog-title" className="card-title">Activities — {activitiesLead.title}</h2>
              <button
                ref={activitiesCloseButton}
                type="button"
                className="icon-button"
                aria-label="Close lead activities"
                onClick={() => setActivitiesLead(null)}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
            <div className="time-modal-body">
              <LeadActivities
                key={activitiesLead.id}
                leadId={activitiesLead.id}
                inlineForm
                onActivitiesChanged={() => { void refreshActivitySummary(activitiesLead.id); }}
              />
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default LeadsPage;
